"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle, AlertCircle, ExternalLink } from "lucide-react";

type ConnectState = "idle" | "validating" | "connected" | "error";

interface StepSocialProps {
  brandId: string;
  onContinue: () => void;
  onSkip: () => void;
  onBack: () => void;
}

interface ConnectedAccount {
  platform: string;
  username: string | null;
  status: string;
}

export function StepSocial({ brandId, onContinue, onSkip, onBack }: StepSocialProps) {
  const { t } = useTranslation("wizard");
  const qc = useQueryClient();
  const [apiKey, setApiKey] = useState("");
  const [connectState, setConnectState] = useState<ConnectState>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [connectedAccounts, setConnectedAccounts] = useState<ConnectedAccount[]>([]);

  async function handleConnect() {
    if (!apiKey.trim()) return;
    setConnectState("validating");
    setErrorMessage(null);
    try {
      // Real connect flow: saves the key on the brand and syncs accounts.
      const res = await fetch(`/api/brands/${brandId}/zernio/connect`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ apiKey: apiKey.trim() }),
      });
      if (!res.ok) {
        let code: string | undefined;
        try {
          code = (await res.json())?.error?.code;
        } catch {
          // Non-JSON error body — fall through to the generic message.
        }
        setErrorMessage(
          code === "ZERNIO_INVALID_KEY"
            ? t("steps.social.errorInvalidKey")
            : t("steps.social.errorUnreachable"),
        );
        setConnectState("error");
        return;
      }

      // Render the real synced account list.
      const accountsRes = await fetch(`/api/brands/${brandId}/accounts`, {
        credentials: "include",
      });
      const accountsBody = accountsRes.ok ? await accountsRes.json() : { data: [] };
      setConnectedAccounts((accountsBody.data ?? []) as ConnectedAccount[]);
      // Keep the rest of the app (e.g. the setup checklist) in sync.
      qc.invalidateQueries({ queryKey: ["accounts", brandId] });
      setConnectState("connected");
    } catch {
      setErrorMessage(t("steps.social.errorUnreachable"));
      setConnectState("error");
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-[18px] font-bold text-foreground tracking-tight">
          {t("steps.social.title")}
        </h2>
        <p className="text-[13px] text-muted-foreground mt-1 leading-relaxed">
          {t("steps.social.description")}
        </p>
      </div>

      <div className="rounded-lg bg-muted border border-border p-4 flex flex-col gap-3">
        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">
          {t("steps.social.howItWorks")}
        </p>
        <div className="flex flex-col gap-2.5">
          {([
            <><a href="https://zernio.com/signup" target="_blank" rel="noreferrer" className="text-brand hover:underline font-medium inline-flex items-center gap-1">{t("steps.social.step1Link")} <ExternalLink size={11} /></a>{t("steps.social.step1Rest")}</>,
            <>{t("steps.social.step2Pre")}<strong>{t("steps.social.step2Bold")}</strong>{t("steps.social.step2Post")}</>,
            <>{t("steps.social.step3")}</>,
          ] as React.ReactNode[]).map((text, i) => (
            <div key={i} className="flex gap-2.5 items-start">
              <span className="w-5 h-5 rounded-full bg-brand-subtle flex items-center justify-center text-[10px] font-bold text-brand-subtle-text flex-shrink-0 mt-0.5">
                {i + 1}
              </span>
              <p className="text-[12px] text-muted-foreground leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="zernio-key">{t("steps.social.apiKeyLabel")}</Label>
        <div className="flex gap-2">
          <Input
            id="zernio-key"
            type="password"
            placeholder="zn_live_••••••••••••••••"
            value={apiKey}
            onChange={(e) => { setApiKey(e.target.value); setConnectState("idle"); }}
            className="flex-1"
          />
          <Button
            type="button"
            onClick={handleConnect}
            disabled={!apiKey.trim() || connectState === "validating"}
          >
            {connectState === "validating" ? t("steps.social.checking") : t("steps.social.connect")}
          </Button>
        </div>
        {connectState === "error" && (
          <p className="text-[11px] text-destructive flex items-center gap-1 mt-0.5">
            <AlertCircle size={11} />
            {errorMessage ?? t("steps.social.errorGeneric")}
          </p>
        )}
        {connectState === "idle" && (
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {t("steps.social.keyHint")}
          </p>
        )}
      </div>

      {connectState === "connected" && (
        <div className="rounded-lg border border-[var(--success-subtle)] bg-[var(--success-subtle)] p-3">
          <p className="text-[11px] font-semibold text-[var(--success)] flex items-center gap-1 mb-2">
            <CheckCircle size={12} /> {t("steps.social.connected")}
          </p>
          {connectedAccounts.length > 0 ? (
            <div className="flex flex-col gap-1">
              {connectedAccounts.map((a, i) => (
                <p key={`${a.platform}-${a.username ?? i}`} className="text-[12px] text-foreground">
                  {a.platform} · {a.username ?? t("steps.social.noUsername")}
                </p>
              ))}
            </div>
          ) : (
            <p className="text-[12px] text-muted-foreground">
              {t("steps.social.noAccountsYet")}
            </p>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 mt-1">
        <Button variant="secondary" onClick={onBack}>{t("nav.back")}</Button>
        <Button className="flex-1" onClick={onContinue} disabled={connectState === "validating"}>
          {t("nav.continue")}
        </Button>
        <Button variant="ghost" className="text-muted-foreground" onClick={onSkip}>
          {t("nav.skip")}
        </Button>
      </div>
    </div>
  );
}
